import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuthUser } from "@/lib/auth-context";
import { safeRedirect } from "@/lib/safe-redirect";
import { CapMark } from "@/components/icons";

/** The signature element: a product-true code window previewing the read → run →
 *  output loop the whole course is built on. Content stays inside lesson-1
 *  concepts (variables + print) — no forward references. */
function LessonPreview(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-card border border-border-subtle bg-card shadow-2xl shadow-primary/30">
      <div className="flex items-center gap-2 border-b border-border-subtle bg-muted px-4 py-2.5">
        <span className="h-3 w-3 rounded-full bg-border" />
        <span className="h-3 w-3 rounded-full bg-border" />
        <span className="h-3 w-3 rounded-full bg-border" />
        <span className="ml-2 font-mono text-xs text-muted-foreground">lesson_01.py</span>
      </div>
      <div className="space-y-1 px-5 py-4 font-mono text-sm leading-relaxed">
        <div className="text-subtle-foreground"># lesson 1 — your first program</div>
        <div>
          <span className="text-foreground">name</span>
          <span className="text-muted-foreground"> = </span>
          <span className="text-success">"future developer"</span>
        </div>
        <div>
          <span className="text-primary">print</span>
          <span className="text-muted-foreground">(</span>
          <span className="text-success">"Welcome,"</span>
          <span className="text-muted-foreground">, </span>
          <span className="text-foreground">name</span>
          <span className="text-muted-foreground">)</span>
        </div>
        <div className="mt-3 border-t border-border-subtle pt-3">
          <div className="mb-1 text-[10px] font-semibold tracking-wide text-subtle-foreground">
            Output
          </div>
          <div className="text-foreground">
            Welcome, future developer
            <span className="ml-0.5 inline-block text-primary motion-safe:animate-pulse">▋</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SignInPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { reload } = useAuthUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/sign-in", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (res.ok) {
        await reload();
        navigate(safeRedirect(params.get("redirect_url")), { replace: true });
      } else {
        const data = (await res.json()) as { error?: { message?: string } };
        setError(data.error?.message ?? "Sign in failed. Please check your credentials.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* Brand hero — desktop. Solid primary keeps white text high-contrast; a
          soft primary glow + white highlight supply the gradient depth. */}
      <section className="relative hidden overflow-hidden bg-primary px-12 py-16 text-primary-foreground lg:flex lg:flex-col lg:justify-center xl:px-16">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-primary-soft/50 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_88%,rgb(255_255_255/0.12),transparent_55%)]"
        />

        <div className="relative max-w-md">
          <div className="auth-rise flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-control bg-primary-foreground/15 ring-1 ring-primary-foreground/20">
              <CapMark className="h-7 w-7 text-primary-foreground" />
            </span>
            <span className="text-xl font-bold tracking-tight">Summer Internship</span>
          </div>

          <h1 className="auth-rise auth-rise-2 mt-10 font-display text-4xl font-extrabold leading-tight tracking-tight xl:text-5xl">
            Learn to code,
            <br />
            one lesson at a time.
          </h1>
          <p className="auth-rise auth-rise-2 mt-5 max-w-sm text-lg leading-relaxed text-primary-foreground/80">
            Read it, run it in your browser, and build a real project — no setup, no installs, no prior experience needed.
          </p>

          <div className="auth-rise auth-rise-3 mt-10">
            <LessonPreview />
          </div>

          <div className="auth-rise auth-rise-4 mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-primary-foreground/70">
            <span>Self-paced</span>
            <span aria-hidden="true">·</span>
            <span>Runs in your browser</span>
            <span aria-hidden="true">·</span>
            <span>Built for absolute beginners</span>
          </div>
        </div>
      </section>

      {/* Form column */}
      <section className="flex min-h-screen flex-col justify-center px-6 py-12 sm:px-10 lg:min-h-0">
        <div className="mx-auto w-full max-w-sm">
          {/* Compact brand lockup — mobile only (the hero carries it on desktop). */}
          <div className="mb-10 flex flex-col items-center text-center lg:hidden">
            <span className="grid h-14 w-14 place-items-center rounded-control bg-gradient-to-br from-primary to-primary-soft text-primary-foreground shadow-lg shadow-primary/30">
              <CapMark className="h-8 w-8" />
            </span>
            <span className="mt-4 text-2xl font-bold tracking-tight text-primary">Summer Internship</span>
            <span className="mt-1 text-sm text-muted-foreground">Learn to code, one lesson at a time.</span>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Sign in</h2>
          <p className="mt-2 text-base text-muted-foreground">
            Welcome back. Pick up right where you left off.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-foreground">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-control border border-border bg-card px-4 py-3 text-base text-foreground placeholder:text-subtle-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-foreground">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary hover:text-primary-soft"
                >
                  Forgot?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-control border border-border bg-card px-4 py-3 text-base text-foreground placeholder:text-subtle-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm font-medium text-destructive">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 rounded-full bg-primary px-4 py-3.5 text-base font-semibold text-primary-foreground transition hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
